/**
 * EmailBroadcastTab — Admin email campaign sender (Phase 1 + 2 + 3)
 *
 * Features:
 *  Phase 1 — Core
 *    - Compose: subject, preheader, heading, HTML body, optional CTA
 *    - Audience selector (all / enrolled / not enrolled) with live count
 *    - Preview: send test email to any address
 *    - Send with confirm dialog, form reset after send
 *    - Broadcast history table (sent / failed / skipped stats)
 *    - Personalization tokens: {{name}}, {{email}}
 *    - Anti-spam: 3/day, 4 h cooldown, 2/user/week (server-side)
 *
 *  Phase 2 — Power features
 *    - Email type selector (announcements / job_alerts / batch_news / promotions / greetings)
 *    - Template Library: save current compose as a template, load saved templates, delete
 *    - Scheduled send: toggle + datetime-local picker; sends at future time via background loop
 *
 *  Phase 3 — Analytics
 *    - Open rate & click rate columns in history (from per-broadcast email_log tracking)
 *    - Retry button for `sent_with_errors` and `failed` broadcasts
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Mail, Send, Eye, RefreshCw, CheckCircle2, XCircle, Clock,
  AlertTriangle, Users, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  Save, Trash2, RotateCcw, CalendarClock, X, FileText,
  BarChart2, MousePointerClick,
} from 'lucide-react';
import { API_BASE, getAdminToken, formatDate } from './adminUtils';

// ── auth helper ───────────────────────────────────────────────────────────────
const authFetch = (url, options = {}) =>
  fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), Authorization: `Bearer ${getAdminToken()}` },
  });

// ── constants ─────────────────────────────────────────────────────────────────
const AUDIENCE_OPTIONS = [
  { value: 'all',          label: 'All active users',              desc: 'Everyone on the platform' },
  { value: 'enrolled',     label: 'Enrolled students only',        desc: 'Users with at least one active course' },
  { value: 'not_enrolled', label: 'Not yet enrolled (cold leads)', desc: 'Registered but no active course' },
];

const EMAIL_TYPES = [
  { value: 'announcements', label: '📢 Announcements', desc: 'Platform news & updates' },
  { value: 'batch_news',    label: '🎓 Batch News',    desc: 'Course batch updates' },
  { value: 'promotions',    label: '🎁 Promotions',    desc: 'Offers & discounts' },
  { value: 'job_alerts',    label: '💼 Job Alerts',    desc: 'Career opportunities' },
  { value: 'greetings',     label: '🎉 Greetings',     desc: 'Festivals & occasions' },
];

const STATUS_STYLE = {
  sending:          'bg-blue-500/20 text-blue-300 border-blue-500/30',
  scheduled:        'bg-purple-500/20 text-purple-300 border-purple-500/30',
  sent:             'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  sent_with_errors: 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  failed:           'bg-rose-500/20 text-rose-300 border-rose-500/30',
};

const TOKEN_CHIPS = [
  { token: '{{name}}',  hint: 'First name' },
  { token: '{{email}}', hint: 'Email address' },
];

// ── small helper components ───────────────────────────────────────────────────
const StatBadge = ({ label, value, color = 'text-slate-300' }) => (
  <div className="text-center">
    <p className={`text-lg font-bold ${color}`}>{value}</p>
    <p className="text-[10px] text-slate-500 mt-0.5">{label}</p>
  </div>
);

const RateBadge = ({ label, rate, color }) => {
  if (rate == null) return null;
  return (
    <div className="text-center">
      <p className={`text-sm font-bold ${color}`}>{rate}%</p>
      <p className="text-[10px] text-slate-500 mt-0.5">{label}</p>
    </div>
  );
};

const ConfirmDialog = ({ open, data, onConfirm, onCancel, loading }) => {
  if (!open) return null;
  const emailTypeLabel = EMAIL_TYPES.find(t => t.value === data.email_type)?.label || data.email_type;
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
            <Mail className="w-5 h-5 text-orange-400" />
          </div>
          <div>
            <h3 className="text-white font-bold text-lg">Confirm Email Broadcast</h3>
            <p className="text-slate-400 text-sm">This will send real emails to your students.</p>
          </div>
        </div>

        <div className="bg-slate-700/50 rounded-xl p-4 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-slate-400">Subject</span>
            <span className="text-white font-medium truncate max-w-[200px]">{data.subject}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Type</span>
            <span className="text-white font-medium">{emailTypeLabel}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Audience</span>
            <span className="text-white font-medium capitalize">{data.audience?.replace('_', ' ')}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">Recipients</span>
            <span className="text-emerald-400 font-bold">{data.estimated} users</span>
          </div>
          {data.scheduled_at && (
            <div className="flex justify-between">
              <span className="text-slate-400">Scheduled for</span>
              <span className="text-purple-400 font-medium">{new Date(data.scheduled_at).toLocaleString()}</span>
            </div>
          )}
        </div>

        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
          <p className="text-amber-300 text-xs flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            {data.scheduled_at
              ? 'This broadcast will be queued and sent at the scheduled time.'
              : 'Sending cannot be undone. Make sure you\'ve sent a preview first and reviewed the content.'}
          </p>
        </div>

        <div className="flex gap-3 pt-1">
          <button onClick={onCancel} disabled={loading}
            className="flex-1 py-2.5 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors text-sm font-medium">
            Cancel
          </button>
          <button onClick={onConfirm} disabled={loading}
            className="flex-1 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold transition-colors flex items-center justify-center gap-2">
            {loading ? (
              <><div className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> {data.scheduled_at ? 'Scheduling…' : 'Sending…'}</>
            ) : (
              data.scheduled_at
                ? <><CalendarClock className="w-4 h-4" /> Schedule</>
                : <><Send className="w-4 h-4" /> Send Now</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Opens Detail Modal ────────────────────────────────────────────────────────
const OpensModal = ({ open, data, onClose }) => {
  const [search, setSearch] = useState('');
  if (!open || !data) return null;

  const filtered = (data.rows || []).filter((r) => {
    const q = search.toLowerCase();
    return !q || r.email?.toLowerCase().includes(q) || r.name?.toLowerCase().includes(q);
  });

  const fmt = (dt) => {
    if (!dt) return '—';
    try { return new Date(dt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch { return dt; }
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-700">
          <div>
            <h3 className="text-white font-bold text-lg flex items-center gap-2">
              <BarChart2 className="w-5 h-5 text-orange-400" /> Open & Click Detail
            </h3>
            <p className="text-slate-400 text-xs mt-0.5 truncate max-w-md">{data.subject}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white mt-0.5">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-4 gap-3 px-6 py-4 border-b border-slate-700/50">
          {[
            { label: 'Total Sent',   value: data.total,      color: 'text-white' },
            { label: 'Opened',       value: data.opened,     color: 'text-sky-400' },
            { label: 'Clicked',      value: data.clicked,    color: 'text-violet-400' },
            { label: 'Open Rate',    value: `${data.open_rate}%`, color: 'text-emerald-400' },
          ].map((s) => (
            <div key={s.label} className="bg-slate-700/40 rounded-xl p-3 text-center">
              <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
              <p className="text-xs text-slate-500 mt-0.5">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b border-slate-700/50">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            className="w-full bg-slate-700/60 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50"
          />
        </div>

        {/* Table */}
        <div className="overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <p className="text-center text-slate-500 text-sm py-10">
              {data.rows?.length === 0 ? 'No email log entries yet — tracking starts after first open.' : 'No results found.'}
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-800/95">
                <tr className="border-b border-slate-700/50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Recipient</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-400">Opened</th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-400">Clicked</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Opened At</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Clicked At</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-700/30">
                {filtered.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-700/20 transition-colors">
                    <td className="px-4 py-3">
                      <p className="text-white text-sm font-medium">{r.name || '—'}</p>
                      <p className="text-xs text-slate-500">{r.email}</p>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {r.opened
                        ? <span className="inline-flex items-center gap-1 text-xs text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /> Yes</span>
                        : <span className="text-xs text-slate-600">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {r.clicked
                        ? <span className="inline-flex items-center gap-1 text-xs text-violet-400"><MousePointerClick className="w-3.5 h-3.5" /> Yes</span>
                        : <span className="text-xs text-slate-600">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-400">{fmt(r.opened_at)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs text-slate-400">{fmt(r.clicked_at)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-700/50 text-xs text-slate-600">
          Showing {filtered.length} of {data.rows?.length || 0} recipients · Opens tracked via email pixel
        </div>
      </div>
    </div>
  );
};

// ── Save Template Dialog ──────────────────────────────────────────────────────
const SaveTemplateDialog = ({ open, onSave, onClose }) => {
  const [name, setName] = useState('');
  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-bold">Save as Template</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-4 h-4" /></button>
        </div>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Template name, e.g. Welcome Series"
          maxLength={80}
          autoFocus
          className="w-full bg-slate-700/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50"
        />
        <div className="flex gap-3">
          <button onClick={onClose}
            className="flex-1 py-2 rounded-lg border border-slate-600 text-slate-300 hover:bg-slate-700 transition-colors text-sm">
            Cancel
          </button>
          <button
            onClick={() => { if (name.trim()) { onSave(name.trim()); setName(''); } else toast.error('Name required'); }}
            className="flex-1 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium transition-colors">
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// ── schedule picker (custom calendar + time selects) ─────────────────────────
export const SCHEDULE_MIN_LEAD_MS = 5 * 60 * 1000; // backend rejects past times; UI enforces ≥5 min

const DOW_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MINUTE_OPTIONS = [0, 15, 30, 45];

const scheduleSelectCls =
  'bg-slate-700/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white ' +
  'focus:outline-none focus:ring-1 focus:ring-orange-500/50';

const SchedulePicker = ({ value, onChange }) => {
  // Default: next full hour from now (10:23 → 11:00); rolls the date past midnight
  const [initial] = useState(() => {
    if (value instanceof Date) return value;
    const d = new Date();
    d.setMinutes(0, 0, 0);
    d.setHours(d.getHours() + 1);
    return d;
  });

  const [selDate, setSelDate] = useState(
    () => new Date(initial.getFullYear(), initial.getMonth(), initial.getDate()),
  );
  const [hour12, setHour12] = useState(((initial.getHours() + 11) % 12) + 1);
  const [minute, setMinute] = useState(MINUTE_OPTIONS.includes(initial.getMinutes()) ? initial.getMinutes() : 0);
  const [ampm, setAmpm] = useState(initial.getHours() >= 12 ? 'PM' : 'AM');

  const [viewYear, setViewYear] = useState(initial.getFullYear());
  const [viewMonth, setViewMonth] = useState(initial.getMonth());
  const [yearGridOpen, setYearGridOpen] = useState(false);

  const hour24 = ampm === 'PM' ? (hour12 % 12) + 12 : hour12 % 12;
  const composed = new Date(
    selDate.getFullYear(), selDate.getMonth(), selDate.getDate(), hour24, minute, 0, 0,
  );
  const isValid = composed.getTime() >= Date.now() + SCHEDULE_MIN_LEAD_MS;

  // Push the composed Date up whenever any part changes (incl. initial mount)
  useEffect(() => {
    onChange(composed);
    // eslint-disable-next-line
  }, [selDate, hour12, minute, ampm]);

  // 6-week grid: leading/trailing overflow days shown muted for context
  const todayMid = new Date();
  todayMid.setHours(0, 0, 0, 0);
  const firstOfMonth = new Date(viewYear, viewMonth, 1);
  const gridStart = new Date(viewYear, viewMonth, 1 - firstOfMonth.getDay());
  const cells = Array.from({ length: 42 }, (_, i) => {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    return d;
  });

  const sameDay = (a, b) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

  const navMonth = (delta) => {
    const d = new Date(viewYear, viewMonth + delta, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const pickDay = (d) => {
    if (d < todayMid) return; // past dates not clickable
    setSelDate(new Date(d.getFullYear(), d.getMonth(), d.getDate()));
    if (d.getMonth() !== viewMonth || d.getFullYear() !== viewYear) {
      setViewYear(d.getFullYear());
      setViewMonth(d.getMonth());
    }
  };

  const thisYear = new Date().getFullYear();
  const yearOptions = [0, 1, 2, 3].map((i) => thisYear + i);
  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString('en-IN', { month: 'long' });

  const confirmLine =
    `${composed.toLocaleDateString('en-IN', { weekday: 'long' })}, ` +
    `${composed.getDate()} ${composed.toLocaleDateString('en-IN', { month: 'long' })} ${composed.getFullYear()} ` +
    `at ${hour12}:${String(minute).padStart(2, '0')} ${ampm} IST`;

  return (
    <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 space-y-4 max-w-md">
      {/* Row 1 — calendar */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <button type="button" onClick={() => navMonth(-1)}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button type="button" onClick={() => setYearGridOpen((v) => !v)}
            className="text-sm font-semibold text-white px-3 py-1 rounded-lg hover:bg-slate-700 transition-colors">
            {monthLabel} <span className="text-orange-400">{viewYear}</span>
          </button>
          <button type="button" onClick={() => navMonth(1)}
            className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-700 rounded-lg transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {yearGridOpen ? (
          <div className="grid grid-cols-4 gap-2 py-4">
            {yearOptions.map((y) => (
              <button key={y} type="button"
                onClick={() => { setViewYear(y); setYearGridOpen(false); }}
                className={`py-2.5 rounded-lg text-sm transition-colors ${
                  y === viewYear
                    ? 'bg-orange-600 text-white font-semibold'
                    : 'text-slate-300 bg-slate-700/50 hover:bg-slate-700'
                }`}>
                {y}
              </button>
            ))}
          </div>
        ) : (
          <>
            <div className="grid grid-cols-7 mb-1">
              {DOW_LABELS.map((d) => (
                <span key={d} className="text-center text-[11px] text-slate-500 font-medium py-1">{d}</span>
              ))}
            </div>
            <div className="grid grid-cols-7 gap-y-0.5">
              {cells.map((d) => {
                const inMonth = d.getMonth() === viewMonth && d.getFullYear() === viewYear;
                const isPast = d < todayMid;
                const isToday = sameDay(d, todayMid);
                const isSel = sameDay(d, selDate);
                let cls = 'w-9 h-9 mx-auto flex items-center justify-center rounded-lg text-sm transition-colors ';
                if (isSel) {
                  cls += 'bg-orange-600 text-white font-semibold';
                } else if (isPast) {
                  cls += 'text-slate-700 cursor-not-allowed';
                } else {
                  cls += (inMonth ? 'text-slate-200' : 'text-slate-600') + ' hover:bg-slate-700 cursor-pointer';
                }
                if (isToday && !isSel) cls += ' ring-1 ring-orange-500';
                return (
                  <button key={d.toISOString()} type="button" disabled={isPast}
                    onClick={() => pickDay(d)} className={cls}>
                    {d.getDate()}
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Row 2 — time selects + confirmation */}
      <div className="border-t border-slate-700/50 pt-3 space-y-2.5">
        <div className="flex items-center gap-2">
          <select value={hour12} onChange={(e) => setHour12(Number(e.target.value))} className={scheduleSelectCls}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map((h) => (
              <option key={h} value={h}>{String(h).padStart(2, '0')}</option>
            ))}
          </select>
          <span className="text-slate-500">:</span>
          <select value={minute} onChange={(e) => setMinute(Number(e.target.value))} className={scheduleSelectCls}>
            {MINUTE_OPTIONS.map((m) => (
              <option key={m} value={m}>{String(m).padStart(2, '0')}</option>
            ))}
          </select>
          <select value={ampm} onChange={(e) => setAmpm(e.target.value)} className={scheduleSelectCls}>
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
          <span className="text-xs text-slate-500 font-medium">IST</span>
        </div>
        <p className={`text-xs font-medium ${isValid ? 'text-emerald-400' : 'text-red-400'}`}>
          {isValid
            ? `Scheduled for: ${confirmLine}`
            : `Scheduled for: ${confirmLine} — must be at least 5 minutes from now`}
        </p>
      </div>
    </div>
  );
};

// ── main component ────────────────────────────────────────────────────────────
export const EmailBroadcastTab = () => {
  // ── form state ────────────────────────────────────────────────────────────
  const [subject,    setSubject]    = useState('');
  const [preheader,  setPreheader]  = useState('');
  const [heading,    setHeading]    = useState('');
  const [bodyHtml,   setBodyHtml]   = useState('');
  const [ctaText,    setCtaText]    = useState('');
  const [ctaUrl,     setCtaUrl]     = useState('');
  const [audience,   setAudience]   = useState('all');
  const [emailType,  setEmailType]  = useState('announcements');
  const [previewEmail, setPreviewEmail] = useState('');

  // ── scheduling ────────────────────────────────────────────────────────────
  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [scheduleAt,      setScheduleAt]      = useState(null); // JS Date from SchedulePicker

  const scheduleValid =
    !scheduleEnabled ||
    (scheduleAt instanceof Date && scheduleAt.getTime() >= Date.now() + SCHEDULE_MIN_LEAD_MS);

  // ── audience count ────────────────────────────────────────────────────────
  const [audienceCount, setAudienceCount] = useState(null);
  const [countLoading,  setCountLoading]  = useState(false);

  // ── actions ───────────────────────────────────────────────────────────────
  const [previewing,  setPreviewing]  = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [sending,     setSending]     = useState(false);
  const [retryingId,  setRetryingId]  = useState(null);

  // ── history ───────────────────────────────────────────────────────────────
  const [history,      setHistory]      = useState([]);
  const [histLoading,  setHistLoading]  = useState(true);
  const [histExpanded, setHistExpanded] = useState(true);

  // ── template library ─────────────────────────────────────────────────────
  const [templates,       setTemplates]       = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(true);
  const [libExpanded,     setLibExpanded]     = useState(false);
  const [saveDialogOpen,  setSaveDialogOpen]  = useState(false);
  const [deletingTplId,   setDeletingTplId]   = useState(null);

  // ── opens modal ───────────────────────────────────────────────────────────
  const [opensModal,     setOpensModal]     = useState(false);
  const [opensData,      setOpensData]      = useState(null);
  const [opensLoading,   setOpensLoading]   = useState(false);

  // ── fetch audience count whenever audience or emailType changes ───────────
  const fetchAudienceCount = useCallback(async () => {
    setCountLoading(true);
    try {
      const res = await authFetch(
        `${API_BASE}/api/admin/email-broadcast/audience-count?audience=${audience}&email_type=${emailType}`
      );
      if (res.ok) setAudienceCount(await res.json());
    } catch { /* silent */ }
    finally { setCountLoading(false); }
  }, [audience, emailType]);

  useEffect(() => { fetchAudienceCount(); }, [fetchAudienceCount]);

  // ── fetch history ─────────────────────────────────────────────────────────
  const fetchHistory = useCallback(async () => {
    setHistLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/email-broadcast/history?limit=50`);
      if (res.ok) setHistory((await res.json()).broadcasts || []);
      else toast.error('Failed to load broadcast history');
    } catch { toast.error('Failed to load broadcast history'); }
    finally { setHistLoading(false); }
  }, []);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // ── fetch templates ───────────────────────────────────────────────────────
  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/email-broadcast/templates`);
      if (res.ok) setTemplates((await res.json()).templates || []);
    } catch { /* silent */ }
    finally { setTemplatesLoading(false); }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // ── insert token at cursor position in body ───────────────────────────────
  const insertToken = (token) => {
    const el = document.getElementById('eb-body');
    if (!el) { setBodyHtml((p) => p + token); return; }
    const s = el.selectionStart, e = el.selectionEnd;
    setBodyHtml(bodyHtml.slice(0, s) + token + bodyHtml.slice(e));
    setTimeout(() => { el.focus(); el.setSelectionRange(s + token.length, s + token.length); }, 0);
  };

  // ── validation ────────────────────────────────────────────────────────────
  const validate = () => {
    if (!subject.trim())  { toast.error('Subject is required');     return false; }
    if (!heading.trim())  { toast.error('Heading is required');     return false; }
    if (!bodyHtml.trim()) { toast.error('Body content is required');return false; }
    if (ctaText.trim() && !ctaUrl.trim()) {
      toast.error('CTA URL is required when CTA text is set');
      return false;
    }
    if (scheduleEnabled) {
      if (!scheduleAt) { toast.error('Please pick a scheduled date/time'); return false; }
      if (scheduleAt.getTime() < Date.now() + SCHEDULE_MIN_LEAD_MS) {
        toast.error('Scheduled time must be at least 5 minutes from now');
        return false;
      }
    }
    return true;
  };

  // ── load template into composer ───────────────────────────────────────────
  const handleLoadTemplate = (tpl) => {
    setSubject(tpl.subject || '');
    setPreheader(tpl.preheader || '');
    setHeading(tpl.heading || '');
    setBodyHtml(tpl.body_html || '');
    setCtaText(tpl.cta_text || '');
    setCtaUrl(tpl.cta_url || '');
    if (tpl.email_type) setEmailType(tpl.email_type);
    toast.success(`Template "${tpl.name}" loaded`);
    setLibExpanded(false);
  };

  // ── save template ─────────────────────────────────────────────────────────
  const handleSaveTemplate = async (name) => {
    if (!validate()) return;
    try {
      const res = await authFetch(`${API_BASE}/api/admin/email-broadcast/templates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name,
          subject, preheader, heading,
          body_html: bodyHtml,
          cta_text:  ctaText.trim() || null,
          cta_url:   ctaUrl.trim()  || null,
          email_type: emailType,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.detail || 'Failed to save template'); return; }
      toast.success(`Template "${name}" saved`);
      setSaveDialogOpen(false);
      fetchTemplates();
    } catch (err) { toast.error('Network error: ' + (err?.message || '')); }
  };

  // ── delete template ───────────────────────────────────────────────────────
  const handleDeleteTemplate = async (id) => {
    setDeletingTplId(id);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/email-broadcast/templates/${id}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Template deleted'); fetchTemplates(); }
      else { const d = await res.json().catch(() => ({})); toast.error(d.detail || 'Failed to delete'); }
    } catch (err) { toast.error('Network error: ' + (err?.message || '')); }
    finally { setDeletingTplId(null); }
  };

  // ── send preview ──────────────────────────────────────────────────────────
  const handlePreview = async () => {
    if (!validate()) return;
    if (!previewEmail.trim() || !previewEmail.includes('@')) {
      toast.error('Enter a valid email address to receive the preview');
      return;
    }
    setPreviewing(true);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/email-broadcast/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          subject, preheader, heading,
          body_html:    bodyHtml,
          cta_text:     ctaText.trim()  || null,
          cta_url:      ctaUrl.trim()   || null,
          audience,
          email_type:   emailType,
          preview_email: previewEmail.trim(),
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.detail || 'Preview failed'); return; }
      toast.success(`Preview sent to ${d.sent_to} — check your inbox!`);
    } catch (err) { toast.error('Network error: ' + (err?.message || '')); }
    finally { setPreviewing(false); }
  };

  // ── open confirm dialog ───────────────────────────────────────────────────
  const handleSendClick = () => { if (validate()) setConfirmOpen(true); };

  // ── confirmed send ────────────────────────────────────────────────────────
  const handleConfirmedSend = async () => {
    setSending(true);
    try {
      const payload = {
        subject, preheader, heading,
        body_html:  bodyHtml,
        cta_text:   ctaText.trim() || null,
        cta_url:    ctaUrl.trim()  || null,
        audience,
        email_type: emailType,
      };
      if (scheduleEnabled && scheduleAt) payload.schedule_at = new Date(scheduleAt).toISOString();

      const res = await authFetch(`${API_BASE}/api/admin/email-broadcast/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.detail || 'Broadcast failed'); return; }
      toast.success(d.message || (scheduleEnabled ? 'Broadcast scheduled!' : `Broadcast queued for ${d.estimated} users!`));
      setConfirmOpen(false);
      // Reset form
      setSubject(''); setPreheader(''); setHeading('');
      setBodyHtml(''); setCtaText(''); setCtaUrl('');
      setScheduleEnabled(false); setScheduleAt('');
      setTimeout(fetchHistory, 2000);
    } catch (err) { toast.error('Network error: ' + (err?.message || '')); }
    finally { setSending(false); }
  };

  // ── retry broadcast ───────────────────────────────────────────────────────
  const handleRetry = async (id) => {
    setRetryingId(id);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/email-broadcast/${id}/retry`, { method: 'POST' });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.detail || 'Retry failed'); return; }
      toast.success(d.message || 'Retry queued');
      setTimeout(fetchHistory, 1500);
    } catch (err) { toast.error('Network error: ' + (err?.message || '')); }
    finally { setRetryingId(null); }
  };

  // ── view opens detail ─────────────────────────────────────────────────────
  const handleViewOpens = async (id) => {
    setOpensLoading(true);
    setOpensModal(true);
    setOpensData(null);
    try {
      const res = await authFetch(`${API_BASE}/api/admin/email-broadcast/${id}/opens`);
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d.detail || 'Failed to load opens'); setOpensModal(false); return; }
      setOpensData(d);
    } catch (err) { toast.error('Network error: ' + (err?.message || '')); setOpensModal(false); }
    finally { setOpensLoading(false); }
  };

  // ── render ────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-6">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <Mail className="w-5 h-5 text-orange-400" />
            Email Broadcast
          </h2>
          <p className="text-sm text-slate-400 mt-0.5">
            Send campaign emails to students — unsubscribed users are automatically skipped.
          </p>
        </div>
        <button onClick={fetchHistory}
          className="flex items-center gap-2 bg-slate-700/60 hover:bg-slate-700 text-slate-300 text-sm px-3 py-2 rounded-lg transition-colors">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      {/* ── Template Library ────────────────────────────────────────────── */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden">
        <button
          onClick={() => setLibExpanded((p) => !p)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-700/20 transition-colors"
        >
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <FileText className="w-4 h-4 text-slate-400" />
            Template Library
            <span className="text-xs text-slate-500 font-normal">({templates.length} saved)</span>
          </h3>
          {libExpanded
            ? <ChevronUp className="w-4 h-4 text-slate-500" />
            : <ChevronDown className="w-4 h-4 text-slate-500" />
          }
        </button>

        {libExpanded && (
          <div className="border-t border-slate-700/40 p-4">
            {templatesLoading ? (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-orange-500" />
              </div>
            ) : templates.length === 0 ? (
              <p className="text-center text-slate-500 text-sm py-6">
                No templates saved yet. Compose an email and click "Save as Template".
              </p>
            ) : (
              <div className="space-y-2">
                {templates.map((tpl) => (
                  <div key={tpl.id}
                    className="flex items-center gap-3 bg-slate-700/30 hover:bg-slate-700/50 rounded-xl px-4 py-3 transition-colors">
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{tpl.name}</p>
                      <p className="text-xs text-slate-500 truncate mt-0.5">
                        {tpl.subject}
                        {tpl.email_type && <span className="ml-2 text-slate-600">· {tpl.email_type}</span>}
                      </p>
                    </div>
                    <button
                      onClick={() => handleLoadTemplate(tpl)}
                      className="flex-shrink-0 text-xs px-3 py-1.5 rounded-lg bg-orange-500/20 text-orange-400 hover:bg-orange-500/30 transition-colors font-medium"
                    >
                      Load
                    </button>
                    <button
                      onClick={() => handleDeleteTemplate(tpl.id)}
                      disabled={deletingTplId === tpl.id}
                      className="flex-shrink-0 p-1.5 rounded-lg text-slate-500 hover:text-rose-400 hover:bg-rose-500/10 transition-colors disabled:opacity-40"
                    >
                      {deletingTplId === tpl.id
                        ? <div className="w-4 h-4 border border-rose-400/40 border-t-rose-400 rounded-full animate-spin" />
                        : <Trash2 className="w-4 h-4" />}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── Composer ────────────────────────────────────────────────────── */}
      <div className="bg-slate-800/50 border border-slate-700/50 rounded-2xl p-6 space-y-5">
        <div className="flex items-center justify-between">
          <h3 className="text-white font-semibold flex items-center gap-2 text-sm">
            <Send className="w-4 h-4 text-orange-400" /> Compose Email
          </h3>
          <button
            onClick={() => setSaveDialogOpen(true)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-300 transition-colors"
          >
            <Save className="w-3.5 h-3.5" /> Save as Template
          </button>
        </div>

        {/* Email Type */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">Email Type</label>
          <div className="flex flex-wrap gap-2">
            {EMAIL_TYPES.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setEmailType(t.value)}
                title={t.desc}
                className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${
                  emailType === t.value
                    ? 'bg-orange-500/20 border-orange-500/50 text-orange-300'
                    : 'bg-slate-700/40 border-slate-700 text-slate-400 hover:border-slate-500'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-slate-600">
            Only users who have this email type enabled will receive the broadcast.
          </p>
        </div>

        {/* Subject + Preheader */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
              Subject <span className="text-red-400">*</span>
            </label>
            <input
              type="text" value={subject} onChange={(e) => setSubject(e.target.value)}
              placeholder="e.g. Job Support is Now Live 🚀" maxLength={200}
              className="w-full bg-slate-700/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50"
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
              Inbox Preview Text
              <span className="ml-1 text-slate-600 normal-case">(shows under subject in inbox)</span>
            </label>
            <input
              type="text" value={preheader} onChange={(e) => setPreheader(e.target.value)}
              placeholder="Optional short summary for inbox preview…" maxLength={200}
              className="w-full bg-slate-700/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50"
            />
          </div>
        </div>

        {/* Heading */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
            Email Heading <span className="text-red-400">*</span>
          </label>
          <input
            type="text" value={heading} onChange={(e) => setHeading(e.target.value)}
            placeholder="e.g. 🚀 Job Support is Now Live — Complete Your Profile!" maxLength={200}
            className="w-full bg-slate-700/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50"
          />
        </div>

        {/* Body */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
              Body Content (HTML) <span className="text-red-400">*</span>
            </label>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-slate-500">Insert token:</span>
              {TOKEN_CHIPS.map(({ token, hint }) => (
                <button key={token} type="button" onClick={() => insertToken(token)} title={hint}
                  className="px-2 py-0.5 rounded bg-slate-700 text-orange-400 text-xs font-mono hover:bg-slate-600 transition-colors">
                  {token}
                </button>
              ))}
            </div>
          </div>
          <textarea
            id="eb-body" value={bodyHtml} onChange={(e) => setBodyHtml(e.target.value)}
            rows={10}
            placeholder={`Write HTML content here.\nExample:\n<p>Hi {{name}},</p>\n<p>We are excited to announce...</p>\n<ul>\n  <li>✅ Item one</li>\n  <li>✅ Item two</li>\n</ul>`}
            className="w-full bg-slate-700/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50 font-mono resize-y"
          />
          <p className="text-[11px] text-slate-600">
            Supports HTML tags: &lt;p&gt; &lt;br&gt; &lt;strong&gt; &lt;em&gt; &lt;ul&gt; &lt;li&gt; &lt;a&gt; etc.
            Tokens <span className="text-orange-400 font-mono">{'{{name}}'}</span> and{' '}
            <span className="text-orange-400 font-mono">{'{{email}}'}</span> are replaced per user.
          </p>
        </div>

        {/* CTA Button (optional) */}
        <div className="space-y-1.5">
          <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
            CTA Button <span className="text-slate-600 normal-case">(optional)</span>
          </label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input type="text" value={ctaText} onChange={(e) => setCtaText(e.target.value)}
              placeholder="Button text, e.g. Update My Profile" maxLength={100}
              className="w-full bg-slate-700/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50" />
            <input type="url" value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)}
              placeholder="https://lab.grras.tech/profile" maxLength={500}
              className="w-full bg-slate-700/60 border border-slate-600 rounded-lg px-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50" />
          </div>
        </div>

        {/* Audience */}
        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">Audience</label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {AUDIENCE_OPTIONS.map((opt) => (
              <button key={opt.value} type="button" onClick={() => setAudience(opt.value)}
                className={`flex flex-col items-start p-3 rounded-xl border text-left transition-all ${
                  audience === opt.value
                    ? 'bg-orange-500/15 border-orange-500/50 text-white'
                    : 'bg-slate-700/40 border-slate-700 text-slate-400 hover:bg-slate-700/60'
                }`}>
                <span className="text-sm font-semibold">{opt.label}</span>
                <span className="text-xs opacity-70 mt-0.5">{opt.desc}</span>
              </button>
            ))}
          </div>

          {/* Live recipient count */}
          {audienceCount && (
            <div className="flex items-center gap-4 bg-slate-700/30 rounded-lg px-4 py-3 mt-1">
              <Users className="w-4 h-4 text-slate-400 flex-shrink-0" />
              {countLoading ? (
                <span className="text-slate-400 text-sm">Counting…</span>
              ) : (
                <>
                  <span className="text-sm text-slate-300">
                    <span className="text-white font-bold">{audienceCount.estimated}</span>{' '}
                    eligible recipients (with {emailType.replace('_', ' ')} enabled)
                  </span>
                  {audienceCount.unsubscribed > 0 && (
                    <span className="text-xs text-slate-500">
                      ({audienceCount.unsubscribed} unsubscribed auto-skipped)
                    </span>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Schedule toggle */}
        <div className="space-y-3 pt-1 border-t border-slate-700/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white font-medium flex items-center gap-2">
                <CalendarClock className="w-4 h-4 text-purple-400" />
                Schedule for later
              </p>
              <p className="text-xs text-slate-500 mt-0.5">Send at a specific date and time (IST)</p>
            </div>
            <button type="button" onClick={() => setScheduleEnabled((v) => !v)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${scheduleEnabled ? 'bg-purple-600' : 'bg-slate-700'}`}>
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${scheduleEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          {scheduleEnabled && (
            <SchedulePicker value={scheduleAt} onChange={setScheduleAt} />
          )}
        </div>

        {/* Preview email + Action buttons */}
        <div className="pt-2 border-t border-slate-700/50 space-y-3">
          <div className="flex items-center gap-3">
            <div className="flex-1 space-y-1">
              <label className="text-xs font-medium text-slate-400 uppercase tracking-wide">
                Send Preview To (your email)
              </label>
              <input type="email" value={previewEmail} onChange={(e) => setPreviewEmail(e.target.value)}
                placeholder="your@email.com"
                className="w-full bg-slate-700/60 border border-slate-600 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-orange-500/50" />
            </div>
            <button type="button" onClick={handlePreview} disabled={previewing}
              className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm font-medium transition-colors disabled:opacity-50 mt-5">
              {previewing
                ? <><div className="w-4 h-4 border-2 border-slate-400/40 border-t-slate-200 rounded-full animate-spin" /> Sending…</>
                : <><Eye className="w-4 h-4" /> Send Preview</>}
            </button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button type="button" onClick={handleSendClick} disabled={!scheduleValid}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              {scheduleEnabled
                ? <><CalendarClock className="w-4 h-4" /> Schedule Broadcast</>
                : <><Send className="w-4 h-4" /> Send to {audienceCount ? `${audienceCount.estimated} users` : 'All'}</>}
            </button>
            <p className="text-[11px] text-slate-600 ml-auto">
              Anti-spam: max 3/day · 4 h cooldown · 2 emails/user/week
            </p>
          </div>
        </div>
      </div>

      {/* ── History ─────────────────────────────────────────────────────── */}
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl overflow-hidden">
        <button onClick={() => setHistExpanded((p) => !p)}
          className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-700/20 transition-colors">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <Clock className="w-4 h-4 text-slate-400" />
            Broadcast History
            <span className="text-xs text-slate-500 font-normal">({history.length})</span>
          </h3>
          {histExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
        </button>

        {histExpanded && (
          <>
            {histLoading ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
              </div>
            ) : history.length === 0 ? (
              <p className="text-center text-slate-500 text-sm py-10">No broadcasts sent yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-t border-slate-700/50 bg-slate-800/60">
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Subject</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Type</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-400">Status</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-400">Delivery</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-400">Open / Click</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Sent By</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-400">Date</th>
                      <th className="px-4 py-3 text-center text-xs font-medium text-slate-400">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700/30">
                    {history.map((b) => (
                      <tr key={b.id} className="hover:bg-slate-700/20 transition-colors">
                        {/* Subject */}
                        <td className="px-4 py-3 max-w-[180px]">
                          <p className="text-white text-sm font-medium truncate">{b.subject}</p>
                          <p className="text-xs text-slate-600 capitalize">{b.audience?.replace('_', ' ')}</p>
                        </td>

                        {/* Type */}
                        <td className="px-4 py-3">
                          <span className="text-xs text-slate-400 capitalize">
                            {(b.email_type || 'announcements').replace('_', ' ')}
                          </span>
                        </td>

                        {/* Status */}
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLE[b.status] || 'bg-slate-700/50 text-slate-400 border-slate-700'}`}>
                            {b.status === 'sent'             && <CheckCircle2 className="w-3 h-3" />}
                            {b.status === 'sending'          && <div className="w-3 h-3 border border-blue-400 border-t-transparent rounded-full animate-spin" />}
                            {b.status === 'scheduled'        && <CalendarClock className="w-3 h-3" />}
                            {b.status === 'sent_with_errors' && <AlertTriangle className="w-3 h-3" />}
                            {b.status === 'failed'           && <XCircle className="w-3 h-3" />}
                            {b.status}
                          </span>
                          {b.status === 'scheduled' && b.scheduled_at && (
                            <p className="text-[10px] text-purple-400 mt-0.5">
                              {new Date(b.scheduled_at).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </p>
                          )}
                        </td>

                        {/* Delivery stats */}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-3">
                            <StatBadge label="Sent"    value={b.sent}    color="text-emerald-400" />
                            <StatBadge label="Failed"  value={b.failed}  color={b.failed > 0 ? 'text-rose-400' : 'text-slate-500'} />
                            <StatBadge label="Skipped" value={b.skipped} color="text-slate-400" />
                          </div>
                        </td>

                        {/* Open / Click rate (Phase 3) */}
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-center gap-3">
                            <RateBadge label="Opens"  rate={b.open_rate}  color="text-sky-400" />
                            <RateBadge label="Clicks" rate={b.click_rate} color="text-violet-400" />
                            {b.open_rate == null && b.status !== 'sent' && b.status !== 'sent_with_errors' && (
                              <span className="text-xs text-slate-600">—</span>
                            )}
                          </div>
                        </td>

                        {/* Sent by */}
                        <td className="px-4 py-3">
                          <span className="text-xs text-slate-400">{b.sent_by}</span>
                        </td>

                        {/* Date */}
                        <td className="px-4 py-3">
                          <span className="text-xs text-slate-500">
                            {b.sent_at
                              ? formatDate(b.sent_at)
                              : b.status === 'scheduled'
                              ? <span className="italic text-purple-400/60">Scheduled</span>
                              : <span className="italic">In progress…</span>}
                          </span>
                        </td>

                        {/* Action (Retry + View Opens) */}
                        <td className="px-4 py-3 text-center">
                          <div className="flex flex-col items-center gap-1.5">
                            {/* View Opens button */}
                            {(b.status === 'sent' || b.status === 'sent_with_errors') && (
                              <button
                                onClick={() => handleViewOpens(b.id)}
                                title="View who opened this email"
                                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-sky-500/15 text-sky-400 hover:bg-sky-500/25 transition-colors"
                              >
                                <BarChart2 className="w-3.5 h-3.5" /> Opens
                              </button>
                            )}
                            {/* Retry button */}
                            {(b.status === 'sent_with_errors' || b.status === 'failed') && (
                              <button
                                onClick={() => handleRetry(b.id)}
                                disabled={retryingId === b.id}
                                title="Retry failed sends"
                                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg bg-amber-500/15 text-amber-400 hover:bg-amber-500/25 transition-colors disabled:opacity-40"
                              >
                                {retryingId === b.id
                                  ? <div className="w-3.5 h-3.5 border border-amber-400/40 border-t-amber-400 rounded-full animate-spin" />
                                  : <RotateCcw className="w-3.5 h-3.5" />}
                                Retry
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Dialogs ──────────────────────────────────────────────────────── */}
      <ConfirmDialog
        open={confirmOpen}
        data={{ subject, audience, email_type: emailType, estimated: audienceCount?.estimated ?? 0, scheduled_at: scheduleEnabled ? scheduleAt : null }}
        onConfirm={handleConfirmedSend}
        onCancel={() => setConfirmOpen(false)}
        loading={sending}
      />

      <SaveTemplateDialog
        open={saveDialogOpen}
        onSave={handleSaveTemplate}
        onClose={() => setSaveDialogOpen(false)}
      />

      {/* Opens loading spinner */}
      {opensLoading && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-8 flex flex-col items-center gap-4">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-orange-500" />
            <p className="text-slate-300 text-sm">Loading open details…</p>
          </div>
        </div>
      )}

      <OpensModal
        open={opensModal && !opensLoading}
        data={opensData}
        onClose={() => { setOpensModal(false); setOpensData(null); }}
      />
    </div>
  );
};
